import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, Clock, BadgeCheck, CheckCircle2, Phone, Upload } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { COMPANY, SERVICES } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMINGS = [
  'Standard — I can wait a few days',
  'Priority — Within 24-48 hours',
  'Emergency — As soon as possible',
  'Flexible — No rush, just need a service request',
];

const RequestService = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', service: '', timing: '', address: '', description: '',
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = [
      ['fullName', 'Full Name'],
      ['email', 'Email'],
      ['phone', 'Phone'],
      ['service', 'Service Required'],
      ['timing', 'How Soon Do You Need This'],
      ['address', 'Property Address'],
      ['description', 'Describe the Work Needed'],
    ];
    for (const [key, label] of required) {
      if (!form[key] || !String(form[key]).trim()) {
        toast({ title: `${label} is required`, description: `Please fill in the "${label}" field to continue.`, variant: 'destructive' });
        return;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast({ title: 'Invalid email', description: 'Please enter a valid email address.', variant: 'destructive' });
      return;
    }
    if (!agree) {
      toast({ title: 'Please accept the Terms', description: 'You must agree to the Client Terms & Conditions to continue.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    const postWithRetry = async (url, data, config) => {
      let lastErr = null;
      for (let attempt = 1; attempt <= 3; attempt += 1) {
        try {
          return await axios.post(url, data, config);
        } catch (err) {
          lastErr = err;
          if (err?.response) throw err;
          await new Promise((r) => setTimeout(r, 1200 * attempt));
        }
      }
      throw lastErr;
    };

    try {
      // 1) Submit request data (small & fast — always goes through)
      const res = await postWithRetry(`${API}/service-requests`, { ...form, agreedTerms: true }, { timeout: 30000 });
      const reqId = res?.data?.id;
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // 2) Upload photos as a best-effort follow-up, sent as JSON/base64 (reliable on mobile)
      if (reqId && photos.length > 0) {
        try {
          const toB64 = (file) => new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve({ filename: file.name, content_type: file.type || 'application/octet-stream', data: String(r.result).split(',')[1] });
            r.onerror = reject;
            r.readAsDataURL(file);
          });
          const fileObjs = [];
          for (const p of photos) fileObjs.push(await toB64(p));
          await postWithRetry(`${API}/service-requests/${reqId}/files-json`, { files: fileObjs }, { timeout: 180000 });
        } catch (fileErr) {
          toast({
            title: 'Request received — photos not uploaded',
            description: 'Your request was submitted, but the photo upload failed (large file or weak signal). Please email your photos to general@ealeadsolutions.co.uk.',
          });
        }
      }
    } catch (err) {
      const isNetwork = !err?.response;
      toast({
        title: 'Something went wrong',
        description: err?.response?.data?.detail
          || (isNetwork
            ? 'Network issue — please check your connection and try again. If it keeps failing, reload the page or contact us on WhatsApp.'
            : 'Please try again or contact us on WhatsApp.'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-800 outline-none transition-colors focus:border-[#3a9b5c] focus:ring-2 focus:ring-[#3a9b5c]/20';
  const labelCls = 'mb-1.5 block text-sm font-medium text-gray-700';

  if (submitted) {
    return (
      <section className="bg-[#e9f3ec] py-24">
        <div className="mx-auto max-w-xl px-5 text-center">
          <div className="rounded-2xl bg-white p-10 shadow-md">
            <CheckCircle2 className="mx-auto h-16 w-16 text-[#3a9b5c]" />
            <h1 className="ea-heading mt-5 text-2xl font-bold">Request Received!</h1>
            <p className="mt-3 text-gray-600">Thank you. We’ve received your service request and will respond as soon as possible with a tailored assessment.</p>
            <Link to="/" className="mt-7 inline-block rounded-full bg-[#3a9b5c] px-7 py-3 font-semibold text-white transition-colors hover:bg-[#318150]">Back to Home</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div>
      <section className="ea-hero-gradient py-16">
        <div className="mx-auto max-w-7xl px-5 text-center lg:px-8">
          <h1 className="font-display text-4xl font-extrabold text-white sm:text-5xl">Request a Service</h1>
          <p className="mt-4 text-lg text-white/85">Tell us what you need — we’ll respond with a tailored service request as soon as possible</p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            {[{ i: ShieldCheck, t: 'Vetted & Insured' }, { i: Clock, t: 'Fast Response' }, { i: BadgeCheck, t: 'No Obligation' }].map((b) => {
              const Icon = b.i;
              return (
                <span key={b.t} className="flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-medium text-white">
                  <Icon size={16} /> {b.t}
                </span>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#e9f3ec] py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl bg-white p-8 shadow-md">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input className={inputCls} value={form.fullName} onChange={(e) => update('fullName', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => update('email', e.target.value)} />
              </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Phone *</label>
                <input className={inputCls} value={form.phone} onChange={(e) => update('phone', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Service Required *</label>
                <select className={inputCls} value={form.service} onChange={(e) => update('service', e.target.value)}>
                  <option value="">Choose a service...</option>
                  {SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                  <option value="Other">Other (please describe below)</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>How Soon Do You Need This? *</label>
              <select className={inputCls} value={form.timing} onChange={(e) => update('timing', e.target.value)}>
                <option value="">Select timing...</option>
                {TIMINGS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Property Address *</label>
              <input className={inputCls} value={form.address} onChange={(e) => update('address', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Describe the Work Needed *</label>
              <textarea rows={4} className={inputCls} value={form.description} onChange={(e) => update('description', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Photos (Optional)</label>
              <label className="flex cursor-pointer items-center gap-3 overflow-hidden rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500 hover:border-[#3a9b5c]">
                <Upload size={18} className="shrink-0" /> <span className="truncate">{photos.length > 0 ? photos.map((p) => p.name).join(', ') : 'Click to upload photos'}</span>
                <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => setPhotos(Array.from(e.target.files))} />
              </label>
            </div>

            <label className="flex items-start gap-3 rounded-lg bg-[#e9f3ec] p-4">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-[#3a9b5c]" />
              <span className="text-sm text-gray-700">
                I have read and agree to the <a href="/client-terms?ref=request-form" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#3a9b5c] underline">Client Terms & Conditions</a> *
              </span>
            </label>

            <button type="submit" disabled={submitting} className="w-full rounded-full bg-[#3a9b5c] px-6 py-3.5 font-semibold text-white shadow-md transition-all hover:bg-[#318150] disabled:opacity-60">
              {submitting ? 'Submitting...' : 'Submit Request →'}
            </button>
            <p className="text-center text-xs text-gray-400">Your details are secure and will only be used to arrange your service request. Currently servicing Greater London only.</p>
            <p className="text-center text-sm text-gray-500">Prefer to message us? <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(COMPANY.whatsappMsg)}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#3a9b5c]">Chat on WhatsApp ({COMPANY.whatsappHours})</a></p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default RequestService;
